// src/utils/ResultUtils.js

/**
 * Función para parsear tiempos en segundos.
 *
 * @param {string} timeStr - Tiempo en formato 'MM:SS.ss' o 'HH:MM:SS.ss'.
 * @returns {number} Tiempo en segundos.
 */
export const parseTime = (timeStr) => {
  if (!timeStr || typeof timeStr !== 'string') return NaN;
  const parts = timeStr.split(':').map(part => parseFloat(part));
  if (parts.length === 3) {
    const [hours, minutes, seconds] = parts;
    return hours * 3600 + minutes * 60 + seconds;
  } else if (parts.length === 2) {
    const [minutes, seconds] = parts;
    return minutes * 60 + seconds;
  } else if (parts.length === 1) {
    return parts[0];
  }
  return NaN;
};

/**
 * Formatea el tiempo en segundos a un formato legible.
 *
 * @param {number} seconds - Tiempo en segundos.
 * @returns {string} Tiempo formateado.
 */
export const formatTime = (seconds) => {
  if (isNaN(seconds)) return '-';
  const hrs = Math.floor(seconds / 3600);
  const mins = Math.floor((seconds % 3600) / 60);
  const secs = (seconds % 60).toFixed(2);
  if (hrs > 0) {
    return `${hrs}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(5, '0')}`;
  } else {
    return `${mins}:${secs.toString().padStart(5, '0')}`;
  }
};

// ==========================================
// FUNCIONES DE FORMATEO DE VIENTO
// ==========================================

/**
 * Formatea la entrada de viento en tiempo real mientras el usuario escribe.
 * Formato: ±X.X (con 1 decimal máximo) - AUTO-FORMATEO COMO DISTANCIAS
 *
 * @param {string} input - Entrada del usuario
 * @returns {string} Viento formateado para mostrar en el campo
 */
export const formatWindInput = (input) => {
  if (!input) return '';
  
  // Eliminar caracteres no permitidos (solo dígitos, +, -, .)
  let cleaned = input.replace(/[^\d+\-\.]/g, '');
  
  // Si está vacío después de limpiar, retornar vacío
  if (!cleaned) return '';
  
  // Extraer el signo si existe
  let sign = '';
  if (cleaned.startsWith('+') || cleaned.startsWith('-')) {
    sign = cleaned[0];
    cleaned = cleaned.slice(1);
  }
  
  // Remover cualquier signo adicional que pueda haber quedado en el medio
  cleaned = cleaned.replace(/[+\-]/g, '');
  
  // Si ya tiene punto, mantener el formato existente
  if (cleaned.includes('.')) {
    const [whole, decimal] = cleaned.split('.');
    // Limitar parte entera a máximo 2 dígitos y decimal a 1
    const limitedWhole = whole.slice(0, 2);
    const limitedDecimal = decimal.slice(0, 1);
    cleaned = limitedWhole + '.' + limitedDecimal;
  } else {
    // Auto-formateo para vientos: insertar punto antes del último dígito
    if (cleaned.length >= 2) {
      const whole = cleaned.slice(0, -1);
      const decimal = cleaned.slice(-1);
      cleaned = whole + '.' + decimal;
    }
  }
  
  // Aplicar reglas de signo
  if (cleaned && cleaned !== '.') {
    const numericValue = parseFloat(cleaned);
    
    if (!isNaN(numericValue)) {
      // Si no hay signo explícito y es 0 o positivo, agregar +
      if (!sign && numericValue >= 0) {
        sign = '+';
      }
      // Si hay signo negativo, mantenerlo (incluso para cero)
      if (sign === '-') {
        // Mantener el signo negativo
      }
    }
  }
  
  return sign + cleaned;
};

/**
 * Formatea el viento para mostrar/guardar con formato final estricto ±X.X
 *
 * @param {string} windValue - Valor de viento a formatear
 * @returns {string} Viento en formato ±X.X o cadena vacía si no hay valor
 */
export const formatWindForDisplay = (windValue) => {
  // ✅ CORREGIDO: Si no hay valor, retornar vacío en lugar de "+0.0"
  if (!windValue || windValue.trim() === '') return '';
  
  // Manejar casos especiales de entrada
  let processedValue = windValue.trim();
  
  // Casos especiales: .5 → +0.5, -.5 → -0.5
  if (processedValue === '.5') processedValue = '+0.5';
  if (processedValue === '-.5') processedValue = '-0.5';
  
  // Extraer signo
  let sign = '+';
  if (processedValue.startsWith('-')) {
    sign = '-';
    processedValue = processedValue.slice(1);
  } else if (processedValue.startsWith('+')) {
    processedValue = processedValue.slice(1);
  }
  
  // Limpiar y parsear el valor
  const cleaned = processedValue.replace(/[^\d\.]/g, '');
  let numericValue = parseFloat(cleaned);
  
  // ✅ CORREGIDO: Si no es un número válido, retornar vacío
  if (isNaN(numericValue)) return '';
  
  // Aplicar el signo
  if (sign === '-' && numericValue > 0) {
    numericValue = -numericValue;
  }
  
  // Formatear con 1 decimal y signo apropiado
  const formatted = Math.abs(numericValue).toFixed(1);
  const finalSign = numericValue >= 0 ? '+' : '-';
  
  return finalSign + formatted;
};

/**
 * Valida que el formato de viento sea correcto
 *
 * @param {string} windValue - Valor de viento a validar
 * @returns {boolean} true si el formato es válido (±X.X)
 */
export const isValidWindFormat = (windValue) => {
  return /^[+-]\d{1,2}\.\d{1}$/.test(windValue);
};

// ==========================================
// FUNCIONES PARA EVENTOS VERTICALES
// ==========================================

/**
 * VERSIÓN CORREGIDA: Calcula la mejor marca para eventos verticales (HJ1, PV1)
 * - Si hace fallos en altura superior pero tenía marca previa, mantiene la marca previa
 * - Si nunca franqueó ninguna altura, retorna 'NM'
 * - No retorna 'Foul' a menos que sea eliminación definitiva
 * 
 * @param {string} participantId - ID del participante
 * @param {object} participantsResults - Resultados del participante con estructura: { heights: {}, specialStatus: '' }
 * @param {string} eventType - Tipo de evento ('vertical')
 * @returns {number|string|null} Mejor altura franqueada, estado especial, o 'NM'
 */
export const calculateBestMarkVertical = (participantId, participantsResults, eventType) => {
  if (!participantsResults || eventType !== 'vertical') return null;

  // Solo retornar estados especiales que no sean automáticos del sistema
  const specialStatus = participantsResults.specialStatus;
  if (specialStatus && !['Valid', 'Foul'].includes(specialStatus)) {
    return specialStatus.toUpperCase();
  }

  // Obtener alturas franqueadas con 'O'
  const heights = participantsResults.heights || {};
  const clearedHeights = [];

  Object.entries(heights).forEach(([heightKey, heightData]) => {
    const heightValue = parseFloat(heightKey);
    if (isNaN(heightValue)) return;
    
    const attempts = heightData.attempts || [];
    if (attempts.includes('O')) {
      clearedHeights.push(heightValue);
    }
  });

  // *** CORRECCIÓN: Si no franqueó ninguna altura, retornar 'NM' (No Mark) ***
  if (clearedHeights.length === 0) {
    return 'NM';
  }

  // Retornar la mayor altura franqueada
  return Math.max(...clearedHeights);
};

/**
 * Calcula datos de desempate para eventos verticales
 * 
 * @param {string} participantId - ID del participante
 * @param {object} participantsResults - Resultados del participante
 * @param {number} bestHeight - Mejor altura franqueada
 * @param {Array} allHeights - Array de todas las alturas de la competencia ordenadas
 * @returns {object} { attemptsAtBestHeight, totalFailedAttempts }
 */
export const calculateVerticalTieBreakData = (participantId, participantsResults, bestHeight, allHeights) => {
  if (!participantsResults.heights || bestHeight === 'NM' || typeof bestHeight !== 'number') {
    return { attemptsAtBestHeight: 0, totalFailedAttempts: 0 };
  }

  const heights = participantsResults.heights;
  let attemptsAtBestHeight = 0;
  let totalFailedAttempts = 0;

  // Encontrar el índice de la mejor altura
  const bestHeightIndex = allHeights.findIndex(h => h.value === bestHeight);
  if (bestHeightIndex === -1) return { attemptsAtBestHeight: 0, totalFailedAttempts: 0 };

  // Calcular intentos en la mejor altura
  const bestHeightKey = bestHeight.toFixed(2);
  const bestHeightData = heights[bestHeightKey];
  if (bestHeightData && bestHeightData.attempts) {
    const attempts = bestHeightData.attempts;
    const firstOIndex = attempts.indexOf('O');
    
    // Si es altura de desempate (1 intento)
    if (attempts.length === 1) {
      attemptsAtBestHeight = attempts[0] === 'O' ? 1 : 0;
    } else {
      // Altura normal (hasta 3 intentos)
      attemptsAtBestHeight = firstOIndex !== -1 ? firstOIndex + 1 : 3;
    }
  }

  // Contar fallos totales hasta la mejor altura (inclusive)
  for (let i = 0; i <= bestHeightIndex; i++) {
    const height = allHeights[i];
    const heightKey = height.value.toFixed(2);
    const heightData = heights[heightKey];
    
    if (heightData && heightData.attempts) {
      const attempts = heightData.attempts;
      
      if (height.value < bestHeight) {
        // Contar todos los 'X' en alturas anteriores
        totalFailedAttempts += attempts.filter(a => a === 'X').length;
      } else if (height.value === bestHeight) {
        // En la mejor altura, contar 'X' solo hasta el primer 'O'
        if (height.isTiebreak) {
          // Altura de desempate: contar 'X' directamente
          if (attempts[0] === 'X') totalFailedAttempts++;
        } else {
          // Altura normal: contar 'X' hasta el primer 'O'
          const firstOIndex = attempts.indexOf('O');
          if (firstOIndex !== -1) {
            for (let j = 0; j < firstOIndex; j++) {
              if (attempts[j] === 'X') totalFailedAttempts++;
            }
          }
        }
      }
    }
  }

  return { attemptsAtBestHeight, totalFailedAttempts };
};

/**
 * VERSIÓN CORREGIDA: Asigna lugares para eventos verticales con manejo correcto de NM
 * 
 * @param {Array} participants - Participantes con resultados
 * @param {Array} allHeights - Array de alturas ordenadas de la competencia
 * @param {number} athletesInFinal - Número de atletas que pasan a final
 * @returns {Object} Mapa de participantId a { place, passed }
 */
export const assignPlacesVertical = (participants, allHeights, athletesInFinal) => {
  const placeMapping = {};

  // *** CORRECCIÓN: Filtrar solo participantes con marca numérica válida ***
  const validParticipants = participants.filter(p => {
    const bestMark = calculateBestMarkVertical(p.idNumber, p.results, 'vertical');
    return typeof bestMark === 'number' && bestMark > 0;
  });

  // Calcular datos de desempate para cada participante válido
  const participantsWithTieBreak = validParticipants.map(p => {
    const bestMark = calculateBestMarkVertical(p.idNumber, p.results, 'vertical');
    const { attemptsAtBestHeight, totalFailedAttempts } = calculateVerticalTieBreakData(
      p.idNumber, 
      p.results, 
      bestMark, 
      allHeights
    );
    
    return {
      ...p,
      bestMark,
      attemptsAtBestHeight,
      totalFailedAttempts
    };
  });

  // Ordenar según reglas de desempate
  participantsWithTieBreak.sort((a, b) => {
    // 1. Mayor altura es mejor
    if (a.bestMark !== b.bestMark) {
      return b.bestMark - a.bestMark;
    }
    
    // 2. Menos intentos en la altura de empate
    if (a.attemptsAtBestHeight !== b.attemptsAtBestHeight) {
      return a.attemptsAtBestHeight - b.attemptsAtBestHeight;
    }
    
    // 3. Menos fallos totales
    if (a.totalFailedAttempts !== b.totalFailedAttempts) {
      return a.totalFailedAttempts - b.totalFailedAttempts;
    }
    
    // 4. Empate total
    return 0;
  });

  // Asignar lugares con manejo de empates
  let currentPlace = 1;
  participantsWithTieBreak.forEach((participant, index) => {
    if (index === 0) {
      placeMapping[participant.idNumber] = { 
        place: currentPlace, 
        passed: currentPlace <= athletesInFinal 
      };
    } else {
      const prevParticipant = participantsWithTieBreak[index - 1];
      
      // Verificar si hay empate total
      const isTied = (
        participant.bestMark === prevParticipant.bestMark &&
        participant.attemptsAtBestHeight === prevParticipant.attemptsAtBestHeight &&
        participant.totalFailedAttempts === prevParticipant.totalFailedAttempts
      );
      
      if (isTied) {
        // Mismo lugar que el anterior
        placeMapping[participant.idNumber] = { 
          place: currentPlace, 
          passed: currentPlace <= athletesInFinal 
        };
      } else {
        // Nuevo lugar
        currentPlace = index + 1;
        placeMapping[participant.idNumber] = { 
          place: currentPlace, 
          passed: currentPlace <= athletesInFinal 
        };
      }
    }
  });

  // *** CORRECCIÓN: Asignar '-' a participantes con NM o estados especiales ***
  participants.forEach(participant => {
    const bestMark = calculateBestMarkVertical(participant.idNumber, participant.results, 'vertical');
    
    if (bestMark === 'NM' || typeof bestMark === 'string' || typeof bestMark !== 'number' || bestMark <= 0) {
      placeMapping[participant.idNumber] = { place: '-', passed: false };
    }
  });

  return placeMapping;
};

// ==========================================
// FUNCIONES PRINCIPALES ACTUALIZADAS
// ==========================================

/**
 * Calcula la mejor marca para un participante.
 * Maneja diferentes tipos de eventos según las reglas de World Athletics.
 *
 * @param {string} participantId - ID del participante.
 * @param {object} participantsResults - Resultados por participante.
 * @param {string} eventType - Tipo de evento ('track', 'horizontal', 'vertical').
 * @returns {number|string|null} Mejor marca como número, estado especial como cadena, o null si no hay una marca válida.
 */
export const calculateBestMark = (participantId, participantsResults, eventType) => {
  if (!participantsResults) return null;

  // Devolver estado especial si existe y no es '-' o 'Valid'
  if (participantsResults.special && participantsResults.special !== '-') {
    return participantsResults.special.toUpperCase();
  }

  // Para eventos verticales, usar la función especializada
  if (eventType === 'vertical') {
    return calculateBestMarkVertical(participantId, participantsResults, eventType);
  }

  if (eventType === 'track') {
    const time = participantsResults.time;
    if (typeof time === 'string' && time.trim() !== '') {
      const specialStatusesTrack = ['DNS', 'DNF', 'DQ', 'SCR', 'FS'];
      if (specialStatusesTrack.includes(time.toUpperCase())) {
        return time.toUpperCase(); // Estado especial
      }
      const timeSeconds = parseTime(time);
      return !isNaN(timeSeconds) ? timeSeconds : null;
    }
    return null;
  }

  if (eventType === 'horizontal') {
    const attempts = Array.isArray(participantsResults.attempts) ? participantsResults.attempts : [];
    const specialStatusesField = ['DNS', 'DNF', 'DQ', 'SCR', 'NM', 'O', 'X'];

    // Filtrar intentos válidos (excluir estados especiales y 'X')
    const validAttempts = attempts.filter(att => {
      if (!att || typeof att.result !== 'string') return false;
      const resultUpper = att.result.toUpperCase();
      return att.result && !specialStatusesField.includes(resultUpper) && resultUpper !== 'X';
    });

    // Obtener marcas numéricas válidas
    const marks = validAttempts
      .map(att => {
        const mark = parseFloat(att.result);
        return isNaN(mark) ? null : mark;
      })
      .filter(mark => mark !== null);

    if (marks.length > 0) {
      // Mayor es mejor para horizontales
      return Math.max(...marks);
    }

    // Verificar si todos los intentos son especiales o 'X'
    const allXOrSpecial = attempts.length > 0 && attempts.every(att => {
      const resultUpper = att.result?.toUpperCase();
      return resultUpper === 'X' || specialStatusesField.includes(resultUpper);
    });

    if (allXOrSpecial) {
      return 'NM'; // No Marca
    }

    return null;
  }

  return null;
};

/**
 * NUEVA FUNCIÓN: Asigna lugares con lógica de desempate para eventos horizontales.
 * - Compara la 1ª mejor marca, luego la 2ª, la 3ª, y así sucesivamente.
 * - Si persiste el empate tras comparar todas las marcas, comparten lugar.
 *
 * @param {Array} participants - Participantes (filtro: special === '-' y bestMark numérico).
 * @param {number} athletesInFinal - Número de atletas que pasan a la final.
 * @returns {Object} placeMapping: { participantId: { place, passed } }
 */
export const assignPlacesHorizontalWithTieBreak = (participants, athletesInFinal) => {
  // 1. Agregar todas las marcas ordenadas desc para cada participante
  const participantsWithMarks = participants.map(part => {
    // Extraer intentos válidos como números
    const validNumericMarks = (part.attempts || [])
      .map(a => parseFloat(a.result))
      .filter(m => !isNaN(m))
      .sort((a, b) => b - a); // Orden descendente

    return {
      ...part,
      allMarksDesc: validNumericMarks,
    };
  });

  // 2. Comparador para desempate
  const compareByAllMarks = (a, b) => {
    const len = Math.max(a.allMarksDesc.length, b.allMarksDesc.length);
    for (let i = 0; i < len; i++) {
      const markA = a.allMarksDesc[i] ?? -Infinity;
      const markB = b.allMarksDesc[i] ?? -Infinity;
      if (markA > markB) return -1;  // 'a' primero
      if (markA < markB) return 1;   // 'b' primero
    }
    // Si han empatado en todos los intentos válidos, es empate total
    return 0;
  };

  // 3. Ordenar aplicando el desempate
  const sorted = [...participantsWithMarks].sort(compareByAllMarks);

  // 4. Asignar lugares con manejo de empates
  const placeMapping = {};
  let currentPlace = 1;

  sorted.forEach((p, idx) => {
    if (idx === 0) {
      placeMapping[p.idNumber] = { place: currentPlace, passed: currentPlace <= athletesInFinal };
    } else {
      const prev = sorted[idx - 1];
      if (compareByAllMarks(p, prev) === 0) {
        // Mismo lugar (empate)
        placeMapping[p.idNumber] = { place: currentPlace, passed: currentPlace <= athletesInFinal };
      } else {
        // Nuevo lugar
        currentPlace = idx + 1;
        placeMapping[p.idNumber] = { place: currentPlace, passed: currentPlace <= athletesInFinal };
      }
    }
  });

  return placeMapping;
};

/**
 * FUNCIÓN ACTUALIZADA: Asigna lugares a todos los participantes de acuerdo a sus mejores marcas.
 * No altera el orden de la lista original y asigna el estado 'passed' basado en athletesInFinal.
 *
 * @param {Array} participants - Array de participantes con sus resultados.
 * @param {string} eventType - Tipo de evento ('track', 'horizontal', 'vertical').
 * @param {boolean} isPreliminary - Indica si la ronda es preliminar.
 * @param {number} athletesInFinal - Número de atletas que pasan a la final.
 * @param {Array} allHeights - Array de alturas para eventos verticales.
 * @returns {Object} Mapa de participantId a { place, passed }.
 */
export const assignPlaces = (participants, eventType, isPreliminary, athletesInFinal, allHeights = []) => {
  // Definir estados especiales
  const specialStatuses = ['DNS', 'DNF', 'DQ', 'SCR', 'NM', 'O'];

  console.log('[assignPlaces] Verificando participantes:', participants.map(p => ({
    idNumber: p.idNumber,
    hasIdNumber: !!p.idNumber,
    eventType,
    bestMark: p.bestMark
  })));

  // Para eventos verticales, usar la función especializada
  if (eventType === 'vertical') {
    return assignPlacesVertical(participants, allHeights, athletesInFinal);
  }

  // Filtrar participantes sin estados especiales y con bestMark numérico
  const validParticipants = participants.filter(participant =>
    participant.special === '-' && typeof participant.bestMark === 'number'
  );

  console.log('[assignPlaces] Participantes válidos:', validParticipants.length, 'de', participants.length);

  // Resultado intermedio (placeMapping) según el tipo de evento
  let placeMapping = {};

  // Lógica diferenciada según el tipo de evento
  if (eventType === 'track') {
    // Orden: menor tiempo es mejor
    const sorted = [...validParticipants].sort((a, b) => a.bestMark - b.bestMark);
    let currentPlace = 1;
    sorted.forEach((participant, index) => {
      if (index === 0) {
        placeMapping[participant.idNumber] = { place: currentPlace, passed: currentPlace <= athletesInFinal };
      } else {
        const prevParticipant = sorted[index - 1];
        if (participant.bestMark === prevParticipant.bestMark) {
          placeMapping[participant.idNumber] = { place: currentPlace, passed: currentPlace <= athletesInFinal };
        } else {
          currentPlace = index + 1;
          placeMapping[participant.idNumber] = { place: currentPlace, passed: currentPlace <= athletesInFinal };
        }
      }
    });
  }
  else if (eventType === 'horizontal') {
    placeMapping = assignPlacesHorizontalWithTieBreak(validParticipants, athletesInFinal);
  }
  else {
    // Otros tipos de eventos (si los hubiera), lógica base
    const sorted = [...validParticipants];
    let currentPlace = 1;
    sorted.forEach((participant) => {
      placeMapping[participant.idNumber] = { place: currentPlace, passed: currentPlace <= athletesInFinal };
      currentPlace++;
    });
  }

  // Finalmente, asignar '-' y 'passed: false' a participantes con estados especiales
  // o sin marcas válidas (e.g., 'NM', bestMark=null, etc.)
  participants.forEach(participant => {
    if (
      participant.special !== '-' ||
      participant.bestMark === null ||
      participant.bestMark === 'NM' ||
      typeof participant.bestMark !== 'number'
    ) {
      placeMapping[participant.idNumber] = { place: '-', passed: false };
    }
  });

  console.log('[assignPlaces] Resultado final:', {
    eventType,
    totalPlaces: Object.keys(placeMapping).length,
    placeMapping
  });

  return placeMapping;
};

/**
 * Calcula los finalistas para un heat específico con lógica de desempate.
 * Excluye a los participantes con opciones especiales como 'DNS', 'DQ', etc.
 *
 * @param {Array} participants - Array de participantes con sus resultados.
 * @param {number} athletesInFinal - Número de atletas que pasan a la final.
 * @returns {Array} Array de finalistas ordenados por mejor marca y con desempate.
 */
export const calculateFinalists = (participants, athletesInFinal) => {
  // Definir estados especiales
  const specialStatuses = ['DNS', 'DNF', 'DQ', 'SCR', 'NM'];

  // Filtrar participantes que no tienen estados especiales y tienen al menos una marca válida
  const validParticipants = participants.filter(participant => {
    if (participant.special && participant.special !== '-') {
      return false;
    }

    // Verificar si tienen al menos una marca válida
    const hasValidMark = (participant.attempts || [])
      .some(att => {
        if (!att || typeof att.result !== 'string') return false;
        const resultUpper = att.result.toUpperCase();
        return att.result && !specialStatuses.includes(resultUpper) && resultUpper !== 'X';
      });

    return hasValidMark;
  });

  // Mapear participantes con sus marcas ordenadas (de mayor a menor para horizontal)
  const participantsWithMarks = validParticipants.map(participant => {
    const marks = (participant.attempts || [])
      .filter(att => {
        if (!att || typeof att.result !== 'string') return false;
        const resultUpper = att.result.toUpperCase();
        // Excluir marcas especiales y 'X'
        return att.result && !specialStatuses.includes(resultUpper) && resultUpper !== 'X';
      })
      .map(att => parseFloat(att.result))
      .filter(mark => !isNaN(mark))
      .sort((a, b) => b - a); // Orden descendente

    return {
      ...participant,
      marks,
    };
  });

  // Función de comparación con lógica de desempate
  const compareParticipants = (a, b) => {
    const len = Math.max(a.marks.length, b.marks.length);
    for (let i = 0; i < len; i++) {
      const markA = a.marks[i] || -Infinity;
      const markB = b.marks[i] || -Infinity;
      if (markA > markB) return -1; // a antes que b
      if (markA < markB) return 1;  // b antes que a
      // si son iguales, continuar
    }
    return 0; // totalmente empatados
  };

  // Ordenar participantes usando la función de comparación con desempate
  const sortedParticipants = participantsWithMarks.sort(compareParticipants);

  // Asignar lugares con manejo de empates
  const finalists = [];
  let currentPlace = 1;

  sortedParticipants.forEach((participant, index) => {
    if (index === 0) {
      finalists.push({ ...participant, place: currentPlace });
    } else {
      const prevParticipant = sortedParticipants[index - 1];
      if (compareParticipants(participant, prevParticipant) === 0) {
        // Empate: asignar el mismo lugar
        finalists.push({ ...participant, place: currentPlace });
      } else {
        // Nuevo lugar
        currentPlace = finalists.length + 1;
        finalists.push({ ...participant, place: currentPlace });
      }
    }
  });

  // Seleccionar los primeros `athletesInFinal` finalistas
  return finalists.slice(0, athletesInFinal);
};